import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    Caption,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {isEmailValid} from "@repo/auth-password";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {RequireReauth} from "@repo/user-settings";

import U13ReminderModal from "#auth/components/u13-reminder-modal.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedFormWrappedTextField from "#components/text-inputs-package/deprecated-form-wrapped-text-field.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";

type OwnProps = {
    identifier: string | null | undefined;
    // If the user is a parent modifying their child's settings.
    // Please note that the child may be over 13.
    isModifyingChild: boolean;
    isClasslinkUser: boolean;
    underAgeGateParentEmail: string | null | undefined;
    managingParentEmail: string | null | undefined;
    pendingParentEmail: string | null | undefined;
    canUpdateParentEmail: boolean;
    daysUntilCutoff: number | null | undefined;
    profileRoot: string;
    successfullyChangedSettings: () => unknown;
};

type Props = OwnProps & WithApolloProps;

type State = {
    newParentEmail: string;
    newParentEmailError: string | null | undefined;
    emailUpdated: boolean;
};

const getTextFieldStyleProps = (isError: boolean) => {
    if (isError) {
        // When this text field is a WonderBlocks component, it will also have
        // a red background on error.
        return {
            focusBorderColor: color.red,
            borderColor: color.red,
        };
    }
    return {focusBorderColor: color.blue};
};

type SetParentErrorCode =
    | "USER_NOT_UNDER_AGE_GATE"
    | "INVALID_PARENT_EMAIL"
    | "USER_HAS_SPAWNING_PARENT"
    | "IS_ALREADY_PARENT"
    | "CANNOT_PARENT_SELF";

type SetParentData = {
    setParent: {
        error:
            | {
                  code:
                      | "UNAUTHORIZED"
                      | "NOT_FOUND"
                      | "NOT_ALLOWED"
                      | SetParentErrorCode;
              }
            | null
            | undefined;
    };
};

type SetParentVariables = {
    parentEmail: string;
};

export const SET_PARENT_MUTATION: GqlOperation<
    SetParentData,
    SetParentVariables
> = gqlOp(gql`
    mutation setParent($parentEmail: String!) {
        setParent(parentEmail: $parentEmail) {
            error {
                code
            }
        }
    }
`);

class ParentEmailSection extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_mounted' has no initializer and is not definitely assigned in the constructor.
    _mounted: boolean;
    state: State = {
        newParentEmail: "",
        newParentEmailError: null,
        emailUpdated: false,
    };

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    safeSetState(newState: Partial<State>, callback?: () => unknown) {
        if (!this._mounted) {
            return;
        }
        // @ts-expect-error - TS2345 - Argument of type 'Partial<State>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
        this.setState(newState, callback);
    }

    validateNewParentEmail: () => void = () => {
        const {newParentEmail} = this.state;
        if (!isEmailValid(newParentEmail)) {
            this.safeSetState({
                newParentEmailError: t`Invalid email`,
            });
        } else {
            this.safeSetState({newParentEmailError: null});
        }
    };

    submitForm: () => void = () => {
        const {successfullyChangedSettings, gqlClient} = this.props;
        const {newParentEmail} = this.state;

        gqlClient
            .mutate(SET_PARENT_MUTATION, {
                variables: {
                    parentEmail: newParentEmail,
                },
            })
            .then((result) => {
                if (result.data?.setParent && !result.data.setParent.error) {
                    successfullyChangedSettings();
                    this.safeSetState({newParentEmail: ""});
                }

                // NOTE(ioana): Current implementation doesn't show errors
                // This should change in redesign.
            })
            .catch(() => {
                // NOTE(ioana): Current implementation doesn't show errors
                // This should change in redesign.
            });
    };

    renderOneParentText(): React.ReactNode {
        const {isModifyingChild, profileRoot} = this.props;
        return (
            <Body>
                <Trans>
                    For privacy reasons, accounts can only have one parent or
                    guardian. Add additional coaches without account privileges
                    in the{" "}
                    <Link
                        href={
                            isModifyingChild
                                ? profileRoot + "coaches"
                                : "/coaches"
                        }
                    >
                        Coaches tab
                    </Link>
                    .
                </Trans>
            </Body>
        );
    }

    renderUpdateUnderAgeGateParentEmail(): React.ReactNode {
        const {underAgeGateParentEmail, daysUntilCutoff} = this.props;

        // Only add ability to see modal allowing one to change a under 13
        // user's parent email if the update_parent_email_days_left
        // template value is set. When this isn't set, the user should not be
        // able to update their parent email.
        if (daysUntilCutoff && underAgeGateParentEmail) {
            return (
                <ModalLauncher
                    modal={({closeModal}) => (
                        <U13ReminderModal
                            // We don't actually need to provide this string
                            // since the modal is starting on the updateEmail
                            // step.
                            daysLeftString=""
                            parentEmail={underAgeGateParentEmail}
                            initialStep="updateEmail"
                            onUpdateParentEmail={() => {
                                this.safeSetState({emailUpdated: true});
                            }}
                            onClose={() => {
                                closeModal();
                                this.safeSetState({emailUpdated: false});
                            }}
                        />
                    )}
                >
                    {({openModal}) => (
                        <Button
                            onClick={openModal}
                            kind="tertiary"
                            style={styles.updateButton}
                        >
                            {t`Update`}
                        </Button>
                    )}
                </ModalLauncher>
            );
        }
    }

    // The managingParentEmail is the email address of the user that
    // manages the child account.  An Under Age Gate (UAG) user will not
    // have a managing parent email if they are not approved, in this case
    // we will display the UAG parent email address that is authorized to
    // approve the child account.
    renderParentEmailAndUpdate(): React.ReactNode {
        const {
            managingParentEmail,
            underAgeGateParentEmail,
            canUpdateParentEmail,
        } = this.props;

        return (
            <View style={styles.row}>
                <LabelMedium style={styles.verticalCenter}>
                    {t`Connected email`}
                </LabelMedium>
                <Strut size={spacing.large_24} />
                <LabelMedium style={styles.verticalCenter}>
                    {managingParentEmail || underAgeGateParentEmail}
                </LabelMedium>
                <Strut size={spacing.medium_16} />
                {canUpdateParentEmail &&
                    this.renderUpdateUnderAgeGateParentEmail()}
            </View>
        );
    }

    renderSubmitButton(): React.ReactNode {
        const {identifier, isClasslinkUser} = this.props;
        const {newParentEmail} = this.state;
        return (
            <View>
                <RequireReauth
                    identifier={identifier}
                    onSuccess={this.submitForm}
                    onFailure={() => {}}
                    hasFacebookButton={false}
                    hasAppleButton={false}
                    showClasslinkAuthProvider={isClasslinkUser}
                >
                    {(invokeReauth) => (
                        <Button
                            onClick={
                                isEmailValid(newParentEmail)
                                    ? invokeReauth
                                    : () => {}
                            }
                            style={styles.button}
                        >
                            {t`Send request to connect`}
                        </Button>
                    )}
                </RequireReauth>
            </View>
        );
    }

    renderAddParentEmail(): React.ReactNode {
        const {pendingParentEmail} = this.props;
        const {newParentEmailError} = this.state;
        return (
            <View>
                {pendingParentEmail && (
                    <React.Fragment>
                        <View style={[styles.row, styles.rowContainer]}>
                            <LabelMedium>{t`Request sent`}</LabelMedium>
                            <Strut size={spacing.large_24} />
                            <LabelMedium>{pendingParentEmail}</LabelMedium>
                        </View>
                        <Strut size={spacing.small_12} />
                    </React.Fragment>
                )}
                <View style={styles.errorContainer}>
                    <LabelMedium
                        style={styles.label}
                        htmlFor="new-parent-email"
                        tag="label"
                    >
                        {t`New email`}
                    </LabelMedium>
                    <DeprecatedFormWrappedTextField
                        id="new-parent-email"
                        value={this.state.newParentEmail}
                        onChange={(e) =>
                            this.safeSetState({newParentEmail: e.target.value})
                        }
                        onBlur={() => this.validateNewParentEmail()}
                        {...getTextFieldStyleProps(newParentEmailError != null)}
                    />
                    <div aria-live="polite">
                        {newParentEmailError != null && (
                            <Caption style={styles.error}>
                                {newParentEmailError}
                            </Caption>
                        )}
                    </div>
                </View>
                {this.renderSubmitButton()}
            </View>
        );
    }

    render(): React.ReactNode {
        const {managingParentEmail, underAgeGateParentEmail} = this.props;

        return (
            <View role="region" aria-label={t`Parent email settings`}>
                <HeadingSmall style={styles.header} tag="h2">
                    {t`Parent/Guardian email`}
                </HeadingSmall>
                <Strut size={spacing.medium_16} />
                {managingParentEmail || underAgeGateParentEmail ? (
                    <React.Fragment>
                        {this.renderOneParentText()}
                        <Strut size={spacing.small_12} />
                        {this.renderParentEmailAndUpdate()}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Body>
                            {t`Connect a parent or guardian to this account by adding their email. A connected parent or guardian has the ability to change your account settings.`}
                        </Body>
                        <Strut size={spacing.small_12} />
                        {this.renderOneParentText()}
                        <Strut size={spacing.small_12} />
                        {this.renderAddParentEmail()}
                    </React.Fragment>
                )}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    label: {
        color: color.offBlack,
        marginBottom: spacing.xSmall_8,
    },
    row: {
        flexDirection: "row",
    },
    error: {
        color: color.red,
        marginTop: spacing.xxxSmall_4,
    },
    errorContainer: {
        minHeight: spacing.xxxLarge_64 + spacing.xLarge_32,
    },
    rowContainer: {
        paddingBottom: spacing.small_12,
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
    updateButton: {
        // To replicate the LabelMedium line
        height: 22,
    },
});

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof ParentEmailSection,
        React.ComponentProps<typeof ParentEmailSection>
    >
>;
export default withApollo(
    ParentEmailSection,
) as React.ComponentType<ExportProps>;
